import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COLOR_COPY, COLOR_RULE_TYPES, colorForeground, type VisitColors } from '@shared/schedule-colors.ts';
export function useColorCopy() {
  const { i18n } = useTranslation();
  return COLOR_COPY[i18n.language.startsWith('es') ? 'es' : 'en'];
}
export function VisitColorSignals({ colors }: { colors?: VisitColors }) {
  const w = useColorCopy();
  if (!colors) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {[colors.primary, ...colors.indicators]
        .filter((v) => v !== null)
        .map((v) => (
          <span
            key={`${v.type}:${v.key}`}
            className="inline-flex items-center gap-1 text-xs"
            title={`${w[v.type]}: ${v.label || w.unknown}`}
          >
            <span
              aria-hidden="true"
              style={{
                background: v.color,
                width: 10,
                height: 10,
                border: '1px solid currentColor',
                borderRadius: '50%',
              }}
            />
            {v.type === 'first_visit' ? w.first : v.label || w.unknown}
          </span>
        ))}
    </span>
  );
}
export function visitColorStyle(colors?: VisitColors) {
  return colors?.primary
    ? { backgroundColor: colors.primary.color, color: colorForeground(colors.primary.color) }
    : undefined;
}
/** Pairs with `visitColorStyle`: every label inside follows its black or white text. */
export function visitColorClass(colors?: VisitColors) {
  return colors?.primary ? 'visit-colored' : undefined;
}
export function ColorLegend({
  items,
  value,
  onChange,
}: {
  items: { colors?: VisitColors }[];
  value: string;
  onChange: (value: string) => void;
}) {
  const w = useColorCopy();
  type LegendEntry = { type: (typeof COLOR_RULE_TYPES)[number]; label: string; color: string };
  const selectedEntry = useRef<{ key: string; entry: LegendEntry } | null>(null);
  const entries = new Map<string, LegendEntry>();
  for (const item of items)
    for (const signal of [item.colors?.primary, ...(item.colors?.indicators ?? []), ...(item.colors?.legend ?? [])])
      if (signal)
        entries.set(`${signal.type}:${signal.key}`, {
          ...signal,
          label: signal.type === 'first_visit' ? w.first : signal.label || w.unknown,
        });
  for (const item of items)
    for (const type of COLOR_RULE_TYPES)
      for (const key of item.colors?.facts[type] ?? [])
        if (!entries.has(`${type}:${key}`))
          entries.set(`${type}:${key}`, {
            type,
            label: type === 'first_visit' ? w.first : key || w.unknown,
            color: '#475569',
          });
  if (value && !entries.has(value)) {
    const [type, ...key] = value.split(':');
    entries.set(
      value,
      selectedEntry.current?.key === value
        ? selectedEntry.current.entry
        : {
            type: COLOR_RULE_TYPES.find((entry) => entry === type) ?? 'tag',
            label: key.join(':') || w.unknown,
            color: '#475569',
          },
    );
  }
  const active = entries.get(value);
  if (!entries.size) return null;
  return (
    <details className="my-3">
      <summary>
        {w.legend}
        {active ? (
          <span className="ml-2 font-semibold">
            {w[active.type]}: {active.type === 'first_visit' ? w.first : active.label}
          </span>
        ) : null}
      </summary>
      <label className="my-3 flex min-w-0 flex-col gap-2 text-sm font-medium">
        {w.legend}
        <select
          className="field-control schedule-color-filter !mb-0 w-full"
          value={value}
          onChange={(event) => {
            const key = event.target.value;
            const entry = entries.get(key);
            selectedEntry.current = entry ? { key, entry } : null;
            onChange(key);
          }}
        >
          <option value="">{w.all}</option>
          {[...entries].map(([key, v]) => (
            <option key={key} value={key}>
              {w[v.type]}: {v.label}
            </option>
          ))}
        </select>
      </label>
      <ul className="flex flex-wrap gap-3">
        {[...entries].map(([key, v]) => (
          <li key={key}>
            <span
              aria-hidden="true"
              style={{ background: v.color, display: 'inline-block', width: 12, height: 12, borderRadius: '50%' }}
            />{' '}
            {w[v.type]}: {v.label}
          </li>
        ))}
      </ul>
    </details>
  );
}
